import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gmailService } from '@/services/gmailService';
import Icon from '@/components/AppIcon';

/**
 * Gmail OAuth Callback Handler
 * Processes the authorization code from Google and redirects back to CRM
 */
const GmailCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Connecting your Gmail account...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract authorization code from URL
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        // Exchange code for tokens via edge function
        setMessage('Exchanging authorization code...');
        await gmailService.handleAuthCallback(code);

        // Success!
        setStatus('success');
        setMessage('Gmail connected successfully!');
        
        // Redirect back to CRM after 2 seconds
        setTimeout(() => {
          navigate('/app/ugc-crm', { replace: true });
        }, 2000);

      } catch (err) {
        console.error('Gmail OAuth callback error:', err);
        setStatus('error');
        setMessage(err.message || 'Failed to connect Gmail');
        
        // Redirect back to CRM after 3 seconds
        setTimeout(() => {
          navigate('/app/ugc-crm', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Connecting Gmail</h2>
            <p className="text-white/70">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-6">
              <Icon name="Check" size={32} className="text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Success!</h2>
            <p className="text-white/70">{message}</p>
            <p className="text-sm text-white/50 mt-4">Redirecting you back to CRM...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-6">
              <Icon name="AlertCircle" size={32} className="text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Connection Failed</h2>
            <p className="text-white/70 mb-4">{message}</p>
            <button
              onClick={() => navigate('/app/ugc-crm', { replace: true })}
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
            >
              Back to CRM
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GmailCallback;
